import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, CalendarClock, TrendingUp } from "lucide-react";
import DocstateListings from "./DocstateListings";
import { reListings, cap, dl } from "@/lib/docstate";

const PERKS = [
  { icon: TrendingUp, title: "Capital Growth", text: "Buy at launch pricing in Dubai's fastest-appreciating communities." },
  { icon: CalendarClock, title: "Flexible Payment Plans", text: "Interest-free developer plans, with instalments through to handover." },
  { icon: Building2, title: "Direct From Developer", text: "Priority access to Emaar, DAMAC and other leading Dubai developers." },
];

export default function DocstateOffPlan() {
  const [developers, setDevelopers] = useState([]);

  useEffect(() => {
    reListings({ status: "off-plan", limit: 50 })
      .then((d) => {
        const names = Array.from(new Set((d.items || []).map((i) => i.developer).filter(Boolean)));
        setDevelopers(names);
      })
      .catch(() => setDevelopers([]));
  }, []);

  return (
    <div className="ds-bg min-h-screen" data-testid="docstate-off-plan">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[color:var(--ds-hairline)] pt-28 pb-16">
        <div className="absolute inset-0 -z-10 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, var(--ds-gold) 0, transparent 45%)" }} />
        <div className="mx-auto max-w-6xl px-5">
          <p className="ds-eyebrow">New Developments</p>
          <h1 className="ds-display mt-3 max-w-3xl text-4xl font-semibold ds-heading sm:text-5xl">
            Off-Plan Properties in Dubai
          </h1>
          <p className="ds-muted mt-4 max-w-2xl text-base font-light">
            Secure tomorrow's landmark addresses at today's launch prices. Browse off-plan residences and investments from Dubai's most trusted developers, with flexible payment plans through to handover.
          </p>
          {developers.length > 0 && (
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="offplan-developers">
              <span className="ds-eyebrow">Developers:</span>
              {developers.map((d) => (
                <span key={d} className="rounded-full border border-[color:var(--ds-gold)]/30 px-4 py-1.5 text-xs font-medium text-[color:var(--ds-gold-soft)]">{cap(d)}</span>
              ))}
            </div>
          )}
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {PERKS.map((p) => (
              <div key={p.title} className="ds-card p-5">
                <p.icon className="h-6 w-6 text-[color:var(--ds-gold)]" />
                <h3 className="ds-display mt-3 text-lg ds-heading">{p.title}</h3>
                <p className="ds-muted mt-1.5 text-sm font-light">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Off-plan listings */}
      <DocstateListings
        preset={{
          status: "off-plan",
          eyebrow: "Available Now",
          title: "Browse Off-Plan Launches",
          subtitle: "Handover dates, developers and payment plans on every listing.",
        }}
      />

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="ds-card flex flex-col items-start justify-between gap-6 p-8 sm:flex-row sm:items-center">
          <div>
            <h2 className="ds-display text-2xl ds-heading">Want first access to new launches?</h2>
            <p className="ds-muted mt-2 text-sm font-light">Register your interest and our team will share launch prices before they go public.</p>
          </div>
          <Link to={dl("/contact")} className="ds-gold-btn shrink-0 px-7 py-3.5 text-sm" data-testid="offplan-register">Register Interest</Link>
        </div>
      </section>
    </div>
  );
}
